import { useEffect, useRef, useState } from "react";
import { Film } from "lucide-react";
import { Link } from "react-router-dom";
import { RoutineVideoPlayer } from "./RoutineVideoPlayer";
import { RoutineVimeoPlayer } from "./RoutineVimeoPlayer";
import { routineVideoEnabled } from "@/lib/routineFeature";
import { useRoutine } from "@/hooks/useRoutine";
import { useRoutineTask } from "@/lib/routineTasks";
import { RoutineVideoSkeleton } from "./RoutineVideoSkeleton";

export function CompleteRoutineSection(props: Parameters<typeof EnabledCompleteRoutineSection>[0]) {
  return routineVideoEnabled ? <EnabledCompleteRoutineSection key={`${props.playlistId}:${props.shareToken ?? ""}`} {...props} /> : null;
}

function EnabledCompleteRoutineSection({
  playlistId,
  shareToken,
  canGenerate = false,
  suspended = false,
  className = "",
}: {
  playlistId: string;
  shareToken?: string;
  canGenerate?: boolean;
  suspended?: boolean;
  className?: string;
}) {
  const { data, error, refresh } = useRoutine(playlistId, shareToken);
  const task = useRoutineTask();
  const [player, setPlayer] = useState<{
    provider?: "supabase" | "vimeo";
    path: string;
    url: string;
    expires_at: number;
  } | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const vimeoPlaying = useRef(false);
  const job = data?.job;
  const processing = !!job?.id && !["failed", "superseded"].includes(job.phase ?? "") && (
    ["processing", "ready"].includes(job.phase ?? "") ||
    (task?.playlistId === playlistId && !task.busy && !task.error && task.progress.stage === "Your complete routine is processing.")
  );
  const generationPending = !!job?.id && (job.active || processing) && !["failed", "superseded"].includes(job.phase ?? "");
  useEffect(() => {
    if (generationPending || !data?.playback) {
      setPlayer(null);
      vimeoPlaying.current = false;
      return;
    }
    const next = data.playback;
    // Keep an active player stable while signed URLs refresh in the background.
    if (player?.provider === "vimeo" ? !vimeoPlaying.current : !video.current || video.current.paused || !player) {
      if (next.path !== player?.path || player.expires_at - Date.now() < 60000)
        setPlayer(next);
    }
  }, [data, player, generationPending]);
  const acceptLatest = () => {
    if (data?.playback && data.playback.path !== player?.path)
      setPlayer(data.playback);
  };
  if (error && !canGenerate && !data) return null;
  const needsReview = data?.clips.filter((c) => !c.reviewed).length ?? 0;
  const reviewed = !!data?.clips.length && !needsReview;
  const loading = (!data && !error) || (!!data?.playback && !player);
  return (
    <section className={className} aria-label="Complete Routine">
      <h2 className="mb-3 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-medium text-[#321b29]">
        <Film className="h-5 w-5 text-[#762a5c]" />
        Complete Routine
      </h2>
      <div className="overflow-hidden rounded-2xl border border-[#e2dce0] bg-white">
        {generationPending ? (
          <p role="status" className="p-6 text-sm text-gray-600">
            {processing
              ? "Your complete routine video is processing. It will upload automatically when ready. You don’t need to stay on this page."
              : "Your new complete routine video is being created and uploaded. Keep the generation tab open until the upload finishes."}
          </p>
        ) : loading ? <RoutineVideoSkeleton /> : player?.provider === "vimeo" ? (
          <RoutineVimeoPlayer key={player.path} src={player.url} onPlayingChange={(playing) => {
            vimeoPlaying.current = playing;
            if (!playing) acceptLatest();
          }} />
        ) : player ? (
            <RoutineVideoPlayer
              key={player.path}
              ref={video}
              src={player.url}
              onPause={acceptLatest}
              onEnded={acceptLatest}
              onError={() => {
                if (data?.playback && data.playback.url !== player.url)
                  setPlayer(data.playback);
                else void refresh();
              }}
            />
        ) : (
          <div className="p-6 text-sm text-gray-600" role="status">
            {error
              ? "The routine video is temporarily unavailable. Please try again shortly."
              : processing
                ? "Your complete routine is processing."
              : !data
                ? "Loading routine…"
                : !data.clips.length && canGenerate
                  ? "Add tutorials to this performance to create a routine."
                  : needsReview
                    ? `${needsReview} tutorial${needsReview === 1 ? "" : "s"} awaiting trim review.`
                    : data.job?.active
                      ? "Your routine video is being created…"
                      : reviewed
                        ? "Ranges saved. Generate the final video when you’re ready."
                        : "The complete routine will be available shortly."}
          </div>
        )}
        {canGenerate && !generationPending && !loading && (processing ? !!player : ["creating", "uploading"].includes(data?.job?.phase ?? "")) && (
          <p role="status" className="border-t border-[#eee7ec] px-4 py-3 text-sm text-[#762a5c]">
            {processing
              ? "Your complete routine is processing."
              : data?.job?.phase === "creating" ? "Preparing your video upload…" : "Your routine video is uploading. Keep the upload tab open."}
          </p>
        )}
        {canGenerate && data && !generationPending && (
          <div className="border-t border-[#eee7ec] p-4">
            <Link
              role="button"
              to={`/app/dashboard/final-video-editor?playlist_id=${encodeURIComponent(playlistId)}`}
              aria-disabled={suspended || !data.clips.length}
              onClick={(event) => { if (suspended || !data.clips.length) event.preventDefault(); }}
              className={`inline-block rounded-lg bg-[#762a5c] px-4 py-2 text-sm font-semibold text-white ${suspended || !data.clips.length ? "opacity-50" : ""}`}
            >
              {data.has_generated || data.playback || data.current
                ? "Update Complete Routine"
                : "Generate Complete Routine"}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
